import { FormInput, PopupBody, PopupFooter, PopupHeader, PopupWrapper, VerticalForm } from '@/components'
import { ModalLayout } from '@/components/HeadlessUI'
import LoadingButton from '@/components/LoadingButton'
import { MODAL_PANEL_CLASS_MD, MODAL_PLACEMENT_CENTER } from '@/constants'
import { runWithToast } from '@/helpers/asyncToast.helper'
import { licenseAdminApi } from '@/services/licenseAdminApi'
import type { TenantLicenseDetailResponse } from '@/services/licenseTypes'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import type { Resolver } from 'react-hook-form'
import * as yup from 'yup'

interface Props {
	license: TenantLicenseDetailResponse | null
	onClose: () => void
	onSaved: (license: TenantLicenseDetailResponse) => void
}

interface TrialLicenseLimitsFormValues {
	maxUsers: number | undefined
	maxInstances: number | undefined
}

const formInputClass = 'form-input mt-1 w-full'

const emptyNumberToUndefined = (value: unknown, originalValue: unknown) => (originalValue === '' || originalValue === null ? undefined : value)

const EditTrialLicenseLimitsModal = ({ license, onClose, onSaved }: Props) => {
	const [defaultValues, setDefaultValues] = useState<TrialLicenseLimitsFormValues>({
		maxUsers: 1,
		maxInstances: 1,
	})
	const [saving, setSaving] = useState(false)
	const [formKey, setFormKey] = useState(0)

	useEffect(() => {
		if (!license) return
		setDefaultValues({
			maxUsers: license.maxUsers || 1,
			maxInstances: license.maxInstances || 1,
		})
		setFormKey((key) => key + 1)
	}, [license])

	const schemaResolver = useMemo(
		() =>
			yupResolver(
				yup.object({
					maxUsers: yup
						.number()
						.transform(emptyNumberToUndefined)
						.typeError('User count must be a whole number')
						.required('User count is required')
						.integer('User count must be a whole number')
						.moreThan(0, 'User count must be greater than zero')
						.max(100, 'User count cannot be greater than 100'),
					maxInstances: yup
						.number()
						.transform(emptyNumberToUndefined)
						.typeError('Instance count must be a whole number')
						.required('Instance count is required')
						.integer('Instance count must be a whole number')
						.moreThan(0, 'Instance count must be greater than zero')
						.max(100, 'Instance count cannot be greater than 100'),
				})
			) as Resolver<TrialLicenseLimitsFormValues>,
		[]
	)

	const save = async (formData: TrialLicenseLimitsFormValues) => {
		if (!license) return

		setSaving(true)
		await runWithToast(
			async () =>
				licenseAdminApi.updateTrialLimits(license.id, {
					maxUsers: Number(formData.maxUsers),
					maxInstances: Number(formData.maxInstances),
				}),
			{
				onSuccess: (updated) => onSaved(updated),
			}
		)
		setSaving(false)
	}

	return (
		<ModalLayout showModal={!!license} toggleModal={onClose} panelClassName={MODAL_PANEL_CLASS_MD} placement={MODAL_PLACEMENT_CENTER}>
			{license && (
				<PopupWrapper>
					<PopupHeader title={`Edit trial limits — ${license.tenantName}`} onClose={onClose} />
					<VerticalForm<TrialLicenseLimitsFormValues> key={formKey} onSubmit={save} resolver={schemaResolver} defaultValues={defaultValues} formClass="flex min-h-0 flex-1 flex-col">
						<PopupBody>
							<div className="space-y-4">
								<p className="text-sm text-[hsl(var(--muted-foreground))]">
									Update the trial license limits stored on this tenant license. These values are used by license enforcement.
								</p>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<FormInput label="User Count" type="number" name="maxUsers" className={formInputClass} containerClass="form-field block" labelClassName="form-label" min={1} max={100} step={1} required />
									<FormInput label="Instance Count" type="number" name="maxInstances" className={formInputClass} containerClass="form-field block" labelClassName="form-label" min={1} max={100} step={1} required />
								</div>
							</div>
						</PopupBody>
						<PopupFooter>
							<button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
								Cancel
							</button>
							<LoadingButton type="submit" className="btn btn-primary" loading={saving} loadingText="Saving...">
								Save Limits
							</LoadingButton>
						</PopupFooter>
					</VerticalForm>
				</PopupWrapper>
			)}
		</ModalLayout>
	)
}

export default EditTrialLicenseLimitsModal
